import { ReactElement, useCallback, useState } from 'react';
import useDropdownHandler, { DropdownResult } from '../../lib/react/hooks/useDropdownHandler';
import ImageComponent from '@spa/components/image-component/image.component';
import classNames from 'clsx';
import styles from './dropdown.component.module.scss';

interface Props {
  readonly id: string;
  readonly children: Array<ReactElement>;
  readonly placeholder: string | ReactElement;
  readonly onClick:
    | ((clickedItemIdx: number) => void)
    | ((clickedItemIdx: number) => Promise<void>);
  readonly initialSelectedItem?: number;
  readonly showOnTop?: boolean;
  readonly disabled?: boolean;
  readonly stretchToParent?: boolean;
  readonly showIcon?: boolean;
}

export default function DropdownComponent({
  id,
  children,
  onClick,
  placeholder,
  initialSelectedItem,
  showOnTop,
  disabled,
  stretchToParent = false,
  showIcon = true
}: Props): ReactElement {
  const initialSelectedItemWithFb: number = initialSelectedItem ?? -1;
  const [lastInitialSelectedItem, setLastInitialSelectedItem] =
    useState<number>(initialSelectedItemWithFb);
  const [selectedItem, setSelectedItem] = useState<number>(initialSelectedItemWithFb);

  if (lastInitialSelectedItem !== initialSelectedItemWithFb) {
    setSelectedItem(initialSelectedItemWithFb);
    setLastInitialSelectedItem(initialSelectedItemWithFb);
  }

  const {
    dropdownRef,
    isDropdownOpen,
    setIsDropdownOpen,
    onKeyDownHandler,
    onBlurHandler,
    focusedOptionIdx,
    listboxId
  }: DropdownResult<HTMLDivElement> = useDropdownHandler<HTMLDivElement>(
    `${id}-handler`,
    children.length,
    selectedItem
  );

  const dropdownClassNames: string = classNames(styles['dropdown'], {
    [styles['dropdown--opened']]: isDropdownOpen,
    [styles['dropdown--show-on-top']]: showOnTop,
    [styles['dropdown--disabled']]: disabled,
    [styles['dropdown--stretch-to-parent']]: stretchToParent
  });

  const itemClassNames = (itemIdx: number): string =>
    classNames(styles['dropdown__list-item'], {
      [styles['dropdown__list-item--selected']]: focusedOptionIdx === itemIdx
    });

  const handleItemOnClick = async (clickedItemIdx: number): Promise<void> => {
    await onClick(clickedItemIdx);
    setSelectedItem(clickedItemIdx);
    setIsDropdownOpen(false);
  };

  const selectedOption = useCallback((): ReactElement => {
    const selectedOption = children[selectedItem];
    return (
      <div
        data-testid={'dropdown-selected-item'}
        className={classNames('text-lg', 'body-bold', styles['dropdown__selected-item'])}
      >
        {selectedOption ?? placeholder}
      </div>
    );
  }, [children, placeholder, selectedItem]);

  const getOptionId = (optionIdx: number): string => `${listboxId}-option-${optionIdx}`;

  return (
    <div ref={dropdownRef} className={dropdownClassNames} onBlur={onBlurHandler}>
      <button
        type={'button'}
        disabled={disabled}
        onClick={(): void => {
          setIsDropdownOpen(!isDropdownOpen);
        }}
        className={styles['dropdown__main-button']}
        onKeyDown={onKeyDownHandler}
        aria-expanded={isDropdownOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-activedescendant={
          isDropdownOpen && focusedOptionIdx >= 0 ? getOptionId(focusedOptionIdx) : undefined
        }
      >
        <div className={styles['dropdown__content']}>
          {selectedOption()}
          {showIcon && (
            <ImageComponent
              className={styles['dropdown__icon']}
              src={'/assets/icons/dropdown-icon.svg'}
              alt={'dropdown-icon'}
              width={24}
              height={24}
            />
          )}
        </div>
      </button>
      {isDropdownOpen && (
        <div className={styles['dropdown__list']} id={listboxId} role="listbox">
          {children.map((child: ReactElement, childIdx: number) => (
            <button
              type={'button'}
              id={getOptionId(childIdx)}
              className={itemClassNames(childIdx)}
              key={child.key}
              onClick={async (): Promise<void> => await handleItemOnClick(childIdx)}
              role="option"
              aria-selected={selectedItem === childIdx}
            >
              {child}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
